/// <reference types="fhir" />

// Import of actions
import * as ConfigActions from "../actions/config.actions";
import {
  DanishMunicipality,
  bloodSugarMedicineTypes,
  PatientNotificationTypes,
  diabetesTypes,
  EvaluationConsentType,
  PatientListingColumnType,
  EvaluationProjectType,
  FeatureEnabledVocab,
  proTaskConsultationType,
  EvaluationProjectStatus,
  Hcp2FaType
} from "../../models/types";

export interface ConfigState {
  orgResource: fhir.Organization;
  patientSupportPersonName: string;
  patientSupportOrgName: string;
  patientSupportPhoneNumber: string;
  patientSupportEmail: string;
  patientSupportAddress: string;
  hcpSupportPersonName: string;
  hcpSupportOrgName: string;
  hcpSupportPhoneNumber: string;
  hcpSupportEmail: string;
  hcpSupportAddress: string;
  patientSchemaMedicineTypesRequired: boolean;
  patientSchemaMedicineTypesDefault: bloodSugarMedicineTypes[];
  patientSchemaMunicipalityRequired: boolean;
  patientSchemaMunicipalityDefault: DanishMunicipality;
  patientSchemaDiabetesTypeRequired: boolean;
  patientSchemaDiabetesTypeDefault: diabetesTypes;
  patientSchemaDiagnosisYearRequired: boolean;
  patientSchemaConsentToSendSmsEnabled: boolean;
  patientNotificationType: PatientNotificationTypes;
  humanReadableNotificationPlan: string;
  evalProject: EvaluationProjectType;
  evalProjectParticipationStatus: EvaluationProjectStatus;
  evalPatientConsentType: EvaluationConsentType;
  evalPatientConsentQnr: string;
  evalPatientPostProDemoQnr: string;
  evalPatientPostVisitQnr: string;
  evalHcpPostVisitQnr: string;
  evalHcpConsentQnr: string;
  evalHcpBaselineQnr: string;
  evalHcpBaselineExtraQnr: string;
  evalHcpRePatientDemoQnr: string;
  evalDataDownload: FeatureEnabledVocab;
  proTaskAvailableDaysBefore: number;
  ffProTaskConsultationTypeExtEnabled: boolean;
  ffProTaskConsultationTypeExtDefault: proTaskConsultationType;
  ffProTaskConsultationTypeExtOptions: proTaskConsultationType[]; //ff_pro_task_consultation_type_extension_options
  ffDefaultColumnsInPatientListing: PatientListingColumnType[];
  ffDutyOfDisclosurePage: boolean;
  ffHcpUsersPage: FeatureEnabledVocab; // "ff_hcp_users_page"
  ffStatisticsPage: FeatureEnabledVocab; // "ff_statistics_page"
  ffDiagnosisTimepointInDashboard: FeatureEnabledVocab; // ff_diagnosis_timepoint_in_dashboard
  ffHcpProPatch: FeatureEnabledVocab; // ff_hcp_pro_patch
  ffPatientPageDefaultView: FeatureEnabledVocab; //ff_patient_page_default_view
  ffImportFromKihModal: FeatureEnabledVocab; // ff_import_from_kih_modal
  ffCurrentConsultationsPage: FeatureEnabledVocab; // ff_current_consultations_page
  ffTimeOfConsultationExt: FeatureEnabledVocab; // ff_time_of_consultation_ext
  ffSoftRequiredMobileNumber: FeatureEnabledVocab; // ff_soft_required_mobile_number
  ffSsnRegistryIntegration: FeatureEnabledVocab; // ff_ssn_registry_integration
  ffKramBtnInDashboard: FeatureEnabledVocab; // ff_kram_btn_in_dashboard
  ffPartialConsultationCompletion: FeatureEnabledVocab;
  ffHcp2faLogin: Hcp2FaType;
  ffNoShowEboksNotificationEnabled: boolean // based on whether or not eboks_task_no_show_letter_template is present
  ehrMode: boolean;
}

export const initialState: ConfigState = {
  orgResource: null,
  patientSupportPersonName: null,
  patientSupportOrgName: null,
  patientSupportPhoneNumber: null,
  patientSupportEmail: null,
  patientSupportAddress: null,
  hcpSupportPersonName: null,
  hcpSupportOrgName: null,
  hcpSupportPhoneNumber: null,
  hcpSupportEmail: null,
  hcpSupportAddress: null,
  patientSchemaMedicineTypesRequired: null,
  patientSchemaMedicineTypesDefault: null,
  patientSchemaMunicipalityRequired: null,
  patientSchemaMunicipalityDefault: null,
  patientSchemaDiabetesTypeRequired: null,
  patientSchemaDiabetesTypeDefault: null,
  patientSchemaDiagnosisYearRequired: null,
  patientSchemaConsentToSendSmsEnabled: null,
  patientNotificationType: null,
  humanReadableNotificationPlan: null,
  evalProject: null,
  evalProjectParticipationStatus: null,
  evalPatientConsentType: null,
  evalPatientConsentQnr: null,
  evalPatientPostProDemoQnr: null,
  evalPatientPostVisitQnr: null,
  evalHcpPostVisitQnr: null,
  evalHcpConsentQnr: null,
  evalHcpBaselineQnr: null,
  evalHcpBaselineExtraQnr: null,
  evalHcpRePatientDemoQnr: null,
  evalDataDownload: null,
  proTaskAvailableDaysBefore: null,
  ffProTaskConsultationTypeExtEnabled: null,
  ffProTaskConsultationTypeExtDefault: null,
  ffProTaskConsultationTypeExtOptions: [],
  ffDefaultColumnsInPatientListing: null,
  ffDutyOfDisclosurePage: null,
  ffHcpUsersPage: null,
  ffStatisticsPage: null,
  ffDiagnosisTimepointInDashboard: null,
  ffHcpProPatch: null,
  ffPatientPageDefaultView: null,
  ffImportFromKihModal: null,
  ffHcp2faLogin: null,
  ffCurrentConsultationsPage: null,
  ffTimeOfConsultationExt: null,
  ffSoftRequiredMobileNumber: null,
  ffSsnRegistryIntegration: null,
  ffKramBtnInDashboard: null,
  ffPartialConsultationCompletion: null,
  ffNoShowEboksNotificationEnabled: false,
  ehrMode: false
};

export function configReducer(
  state = initialState,
  action: ConfigActions.Actions
) {
  switch (action.type) {
    case ConfigActions.RESET_STATE: {
      return initialState;
    }
    case ConfigActions.UPDATE: {
      return { ...state, ...action.payload };
    }
    default: {
      return state;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/store/reducers/config.reducer.ts